"use client"

import React, { useState, useMemo } from "react"
import { motion, AnimatePresence } from "motion/react"
import Link from "next/link"
import Image from "next/image"
import { Container } from "@/app/(frontend)/components/ui/Container"
import { Badge } from "@/app/(frontend)/components/ui/Badge"
import type { Project, ProjectCategory, Media } from "@/payload-types"

interface AnimatedGalleryProps {
	title: string
	subtitle?: string | null
	projects: Project[]
	categories: ProjectCategory[]
	display: "masonry" | "grid" | "carousel"
	showFilters?: boolean | null
	showViewAll?: boolean | null
}

export const AnimatedGallery: React.FC<AnimatedGalleryProps> = (props) => {
	const {
		title,
		subtitle,
		projects,
		categories,
		display,
		showFilters,
		showViewAll,
	} = props
	const [activeCategory, setActiveCategory] = useState<string>("all")

	const filteredProjects = useMemo(() => {
		if (activeCategory === "all") return projects
		return projects.filter((project) => {
			const categoryId =
				typeof project.category === "object"
					? project.category.id
					: project.category
			return categoryId === activeCategory
		})
	}, [projects, activeCategory])

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
			},
		},
	}

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: { opacity: 1, y: 0 },
		exit: { opacity: 0, scale: 0.95 },
	}

	return (
		<section className="py-20">
			<Container>
				<div className="text-center mb-12">
					<h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
					{subtitle && (
						<p className="text-gray-700 max-w-2xl mx-auto">{subtitle}</p>
					)}
				</div>

				{showFilters && categories.length > 0 && (
					<div className="flex flex-wrap justify-center gap-2 mb-10">
						<button
							type="button"
							onClick={() => setActiveCategory("all")}
							className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
								activeCategory === "all"
									? "bg-primary text-primary-foreground"
									: "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
							}`}
						>
							Todos
						</button>
						{categories.map((category) => (
							<button
								key={category.id}
								type="button"
								onClick={() => setActiveCategory(category.id)}
								className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
									activeCategory === category.id
										? "bg-primary text-primary-foreground"
										: "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
								}`}
							>
								{category.name}
							</button>
						))}
					</div>
				)}

				<motion.div
					layout
					variants={containerVariants}
					initial="hidden"
					animate="visible"
					className={
						display === "masonry"
							? `gap-6 space-y-6 ${
									filteredProjects.length === 1
										? "columns-1 max-w-md mx-auto"
										: filteredProjects.length === 2
											? "columns-1 sm:columns-2 max-w-4xl mx-auto"
											: "columns-1 sm:columns-2 lg:columns-3"
								}`
							: "grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
					}
				>
					<AnimatePresence mode="popLayout">
						{filteredProjects.map((project) => {
							const thumbnail = project.thumbnail as Media
							const category = project.category as ProjectCategory

							return (
								<motion.div
									key={project.id}
									layout
									variants={itemVariants}
									initial="hidden"
									animate="visible"
									exit="exit"
									className={display === "masonry" ? "break-inside-avoid" : ""}
								>
									<Link
										href={`/proyectos/${project.slug}`}
										className="group block relative overflow-hidden rounded-xl bg-card border border-border shadow-sm hover:shadow-md transition-shadow"
									>
										<div className="aspect-[4/3] relative overflow-hidden">
											{thumbnail?.url && (
												<Image
													src={thumbnail.url}
													alt={thumbnail.alt || project.title}
													fill
													className="object-cover transition-transform duration-500 group-hover:scale-110"
												/>
											)}
											<div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
										</div>
										<div className="p-4">
											<div className="flex items-center justify-between mb-2">
												<Badge variant="outline" className="text-[10px]">
													{category?.name}
												</Badge>
												{project.completedDate && (
													<span className="text-[10px] text-gray-600">
														{new Date(project.completedDate).getFullYear()}
													</span>
												)}
											</div>
											<h3 className="text-lg font-semibold group-hover:text-primary transition-colors line-clamp-1">
												{project.title}
											</h3>
										</div>
									</Link>
								</motion.div>
							)
						})}
					</AnimatePresence>
				</motion.div>

				{showViewAll && (
					<div className="mt-12 text-center">
						<Link
							href="/proyectos"
							className="inline-flex items-center justify-center px-6 py-3 rounded-lg border-2 border-primary text-primary font-semibold hover:bg-primary hover:text-primary-foreground transition-all"
						>
							Ver todos los proyectos
						</Link>
					</div>
				)}
			</Container>
		</section>
	)
}
